import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import FirmaDigital from './FirmaDigital';

// ID del formulario de datos del estudiante
const FORMULARIO_DATOS_ESTUDIANTE_ID = "de6750e1-0c28-4c20-9561-9c1492860c41";

// Sub-componente para el paso de Descargar Invoice
const DescargarInvoiceStep = ({ registroActivo, API, handleConfirmarInvoice, handleRetrocederEtapa, procesando }) => {
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(true);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (registroActivo?.id) {
      fetchInvoice();
    }
  }, [registroActivo?.id]);

  const fetchInvoice = async () => {
    try {
      setLoadingInvoice(true);
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const res = await axios.get(`${API}/invoices/registro/${registroActivo.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoiceData(res.data);
    } catch (err) {
      // Invoice might not exist yet
      setInvoiceData(null);
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      setGeneratingInvoice(true);
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      // Call the generate endpoint
      await axios.post(`${API}/estudiantes/generar-mi-invoice/${registroActivo.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice generado correctamente');
      await fetchInvoice();
    } catch (err) {
      console.error('Error generating invoice:', err);
      toast.error('Error al generar invoice. Intenta de nuevo.');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoiceData?.numero_invoice) return;
    try {
      setDownloadingPdf(true);
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const response = await fetch(`${API}/invoices/${invoiceData.numero_invoice}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error descargando');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoiceData.numero_invoice}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice descargado');
    } catch (err) {
      console.error('Error downloading:', err);
      toast.error('Error al descargar el PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-file-download text-3xl text-green-600"></i>
        </div>
        <h2 className="text-xl font-bold text-gray-800">Paso 3: Descarga tu Invoice</h2>
        <p className="text-gray-600">Descarga tu factura y realiza el pago según las instrucciones</p>
      </div>

      {/* Info del registro */}
      {registroActivo && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <i className="fas fa-info-circle text-blue-600 mt-1"></i>
            <div>
              <p className="font-medium text-blue-800">Detalles de tu registro:</p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• Semestre: <strong>{registroActivo.semestre_nombre || registroActivo.semestre}</strong></li>
                <li>• Método de pago: <strong>{registroActivo.metodo_pago}</strong></li>
                <li>• Monto a pagar: <strong>${registroActivo.monto} {registroActivo.moneda}</strong></li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Invoice section */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <i className="fas fa-file-invoice-dollar text-4xl text-green-600"></i>
          </div>

          {loadingInvoice ? (
            <div className="text-gray-600">
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Buscando tu invoice...
            </div>
          ) : invoiceData ? (
            <>
              <div>
                <p className="font-bold text-green-800 text-lg">Invoice #{invoiceData.numero_invoice}</p>
                <p className="text-sm text-green-700 mt-1">Tu factura está lista para descargar</p>
              </div>
              <Button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
                data-testid="descargar-invoice-pdf-btn"
              >
                {downloadingPdf ? (
                  <><i className="fas fa-spinner fa-spin mr-2"></i>Descargando...</>
                ) : (
                  <><i className="fas fa-download mr-2"></i>Descargar Invoice PDF</>
                )}
              </Button>
            </>
          ) : (
            <>
              <div>
                <p className="font-bold text-amber-800 text-lg">Invoice no generado aún</p>
                <p className="text-sm text-amber-700 mt-1">Genera tu factura para ver los datos de pago</p>
              </div>
              <Button
                onClick={handleGenerateInvoice}
                disabled={generatingInvoice}
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3"
                data-testid="generar-invoice-btn"
              >
                {generatingInvoice ? (
                  <><i className="fas fa-spinner fa-spin mr-2"></i>Generando...</>
                ) : (
                  <><i className="fas fa-file-medical mr-2"></i>Generar Invoice</>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Instrucciones */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <i className="fas fa-lightbulb text-yellow-600 mt-1"></i>
          <div>
            <p className="font-medium text-yellow-800">¿Cómo pagar?</p>
            <ol className="text-sm text-yellow-700 mt-2 space-y-1 list-decimal list-inside">
              <li>Descarga el invoice con los datos de pago</li>
              <li>Realiza el pago según el método seleccionado ({registroActivo?.metodo_pago})</li>
              <li>Guarda el comprobante de pago (foto o PDF)</li>
              <li>Vuelve aquí y continúa al siguiente paso</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Botones de navegación */}
      <div className="pt-4 border-t space-y-3">
        <Button
          onClick={handleConfirmarInvoice}
          disabled={procesando}
          className="w-full bg-purple-600 hover:bg-purple-700 py-3"
        >
          {procesando ? (
            <><i className="fas fa-spinner fa-spin mr-2"></i>Procesando...</>
          ) : (
            <><i className="fas fa-arrow-right mr-2"></i>Ya tengo mi comprobante, continuar</>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={handleRetrocederEtapa}
          disabled={procesando}
          className="w-full text-gray-600 hover:text-gray-800"
          data-testid="volver-paso-anterior-btn"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Volver al paso anterior
        </Button>
        <p className="text-xs text-gray-500 text-center mt-2">
          Continúa solo cuando ya hayas realizado el pago y tengas el comprobante
        </p>
      </div>
    </div>
  );
};

const ProcesoRegistro = () => {
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [registroActivo, setRegistroActivo] = useState(null);
  const [semestres, setSemestres] = useState([]);
  const [semestreSeleccionado, setSemestreSeleccionado] = useState(null);
  const [clasesSemestre, setClasesSemestre] = useState([]);
  const [uploadingComprobante, setUploadingComprobante] = useState(false);
  const [inscribiendoClases, setInscribiendoClases] = useState(false);
  const fileInputRef = useRef(null);
  
  // Estado para verificar si necesita llenar formulario de datos
  const [necesitaFormularioDatos, setNecesitaFormularioDatos] = useState(false);
  const [verificandoFormulario, setVerificandoFormulario] = useState(true);

  // Estado para Fase 0 - Selección de semestre
  const [semestresDisponibles, setSemestresDisponibles] = useState([]);
  const [semestrePreSeleccionado, setSemestrePreSeleccionado] = useState(null);
  // NUEVO: para grupos-seleccion (Extracurricular / Abierta / Autocapacitación)
  //   el estudiante elige N clases individuales y paga precio × N.
  const [mostrarSelectorClases, setMostrarSelectorClases] = useState(false);
  const [clasesIdsSeleccionadas, setClasesIdsSeleccionadas] = useState([]);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [loadingSemestres, setLoadingSemestres] = useState(false);
  
  // Estado para Compromiso de Pago
  const [compromisoPagoHabilitado, setCompromisoPagoHabilitado] = useState(false);
  const [compromisoPagoPlazo, setCompromisoPagoPlazo] = useState(15);
  const [pagoCuotas3Habilitado, setPagoCuotas3Habilitado] = useState(false);
  const [aceptaTerminosCompromiso, setAceptaTerminosCompromiso] = useState(false);
  const [firmandoCompromiso, setFirmandoCompromiso] = useState(false);
  const [mostrarFirmaCuotas, setMostrarFirmaCuotas] = useState(false);
  const [firmandoCuotas, setFirmandoCuotas] = useState(false);

  // Estados del proceso - 5 pasos (incluyendo Fase 0)
  const ETAPAS = [
    { id: 'seleccion_semestre', nombre: 'Seleccionar', icono: 'fa-list-check', color: 'indigo' },
    { id: 'seleccion_pago', nombre: 'Forma de Pago', icono: 'fa-credit-card', color: 'blue' },
    { id: 'descargar_invoice', nombre: 'Descargar Invoice', icono: 'fa-file-download', color: 'green' },
    { id: 'esperando_comprobante', nombre: 'Enviar Recibo', icono: 'fa-whatsapp', color: 'green' },
    { id: 'inscribir_clases', nombre: 'Inscribir Clases', icono: 'fa-book', color: 'purple' }
  ];

  const [selectedMetodoPago, setSelectedMetodoPago] = useState('efectivo');
  const [selectedMoneda, setSelectedMoneda] = useState('UYU');
  const [procesando, setProcesando] = useState(false);

  useEffect(() => {
    // Primero verificar si necesita llenar el formulario de datos
    verificarFormularioDatos();
    
    // Leer semestre seleccionado desde localStorage (viene del modal del Dashboard - compatibilidad)
    const semestreGuardado = localStorage.getItem('semestreSeleccionado');
    if (semestreGuardado) {
      try {
        const semestre = JSON.parse(semestreGuardado);
        setSemestreSeleccionado(semestre);
        // Limpiar después de leer
        localStorage.removeItem('semestreSeleccionado');
      } catch (e) {
        console.error('Error parsing semestre:', e);
      }
    }
    loadData();
    loadSemestresDisponibles();
  }, []);

  // Verificar si el usuario necesita llenar el formulario de datos del estudiante
  const verificarFormularioDatos = async () => {
    try {
      setVerificandoFormulario(true);
      
      // Solo verificar para estudiantes (no pastores ni graduados que tienen otro flujo)
      if (user?.rol !== 'estudiante') {
        setNecesitaFormularioDatos(false);
        setVerificandoFormulario(false);
        return;
      }
      
      // Consultar al backend si ya completó el formulario (verifica respuestas reales + auto-corrige)
      try {
        const checkRes = await axios.get(`${API}/users/me/verificar-formulario-registro`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (checkRes.data?.completado) {
          setNecesitaFormularioDatos(false);
          setVerificandoFormulario(false);
          return;
        }
      } catch (err) {
        console.error('Error verificando formulario:', err);
      }
      
      // Verificar si tiene registros previos a semestres (usuario existente)
      const registrosRes = await axios.get(`${API}/mis-registros`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const tieneRegistrosPrevios = registrosRes.data.registros?.some(r => 
        r.etapa_proceso === 'completado' || 
        r.estado_pago === 'aprobado' || 
        r.estado_pago === 'completado' || 
        r.estado_pago === 'pagado'
      );
      
      // Si tiene cualquier registro en proceso, ya pasó por el formulario
      const tieneRegistroEnProceso = registrosRes.data.registros?.some(r => 
        r.etapa_proceso && r.etapa_proceso !== 'completado'
      ) || registrosRes.data.registros?.some(r => 
        r.estado_pago && !['completado', 'aprobado', 'pagado', 'rechazado', 'cancelado'].includes(r.estado_pago)
      );
      
      // Si es usuario nuevo (sin registros previos completados Y sin registros en proceso), pedir formulario
      if (!tieneRegistrosPrevios && !tieneRegistroEnProceso) {
        setNecesitaFormularioDatos(true);
      } else {
        setNecesitaFormularioDatos(false);
      }
      
    } catch (error) {
      console.error('Error verificando formulario de datos:', error);
      setNecesitaFormularioDatos(false);
    } finally {
      setVerificandoFormulario(false);
    }
  };

  // Cargar semestres disponibles para Fase 0 (mismo endpoint que usa el modal)
  const loadSemestresDisponibles = async () => {
    setLoadingSemestres(true);
    try {
      const response = await axios.get(`${API}/estudiantes/estado-renovacion`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.semestres_disponibles) {
        setSemestresDisponibles(response.data.semestres_disponibles);
      }
      
      // Cargar estado de compromiso de pago
      if (response.data.compromiso_pago_habilitado) {
        setCompromisoPagoHabilitado(true);
        setCompromisoPagoPlazo(response.data.compromiso_pago_plazo_max || 15);
      }
      if (response.data.pago_cuotas_3_habilitado) {
        setPagoCuotas3Habilitado(true);
      }
    } catch (error) {
      console.error('Error cargando semestres disponibles:', error);
    } finally {
      setLoadingSemestres(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar registro activo del usuario (si existe)
      const registrosRes = await axios.get(`${API}/mis-registros`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Buscar registro en proceso (no completado)
      let registroEnProceso = registrosRes.data.registros?.find(r => 
        r.etapa_proceso && r.etapa_proceso !== 'completado'
      );
      
      // También buscar registros sin etapa_proceso pero con estado activo (registros legacy/atascados)
      if (!registroEnProceso) {
        const registroSinEtapa = registrosRes.data.registros?.find(r => 
          !r.etapa_proceso && r.estado_pago && 
          !['completado', 'aprobado', 'pagado', 'rechazado', 'cancelado'].includes(r.estado_pago)
        );
        if (registroSinEtapa) {
          // Asignar etapa según el estado del pago
          let etapaInferida = 'descargar_invoice';
          if (registroSinEtapa.estado_pago === 'comprobante_subido' || registroSinEtapa.estado_pago === 'comprobante_pendiente') {
            etapaInferida = 'esperando_comprobante';
          } else if (registroSinEtapa.estado_pago === 'invoice_enviado' || registroSinEtapa.estado_pago === 'pendiente') {
            etapaInferida = 'descargar_invoice';
          }
          registroSinEtapa.etapa_proceso = etapaInferida;
          registroEnProceso = registroSinEtapa;
        }
      }
      
      if (registroEnProceso) {
        setRegistroActivo(registroEnProceso);
        
        // Si el registro está en seleccion_pago, reconstruir semestreSeleccionado desde los datos del registro
        if (registroEnProceso.etapa_proceso === 'seleccion_pago' && registroEnProceso.semestre_id) {
          setSemestreSeleccionado({
            id: registroEnProceso.semestre_id,
            nombre: registroEnProceso.semestre_nombre || 'Semestre',
            name: registroEnProceso.semestre_nombre || 'Semestre',
            precio_uyu: registroEnProceso.monto && registroEnProceso.moneda === 'UYU' ? registroEnProceso.monto : 0,
            precio_usd: registroEnProceso.monto && registroEnProceso.moneda === 'USD' ? registroEnProceso.monto : 0,
            price_uyu: registroEnProceso.monto && registroEnProceso.moneda === 'UYU' ? registroEnProceso.monto : 0,
            price_usd: registroEnProceso.monto && registroEnProceso.moneda === 'USD' ? registroEnProceso.monto : 0
          });
          if (registroEnProceso.metodo_pago) {
            setSelectedMetodoPago(registroEnProceso.metodo_pago);
          }
        }
        
        // Si es 3 cuotas y aún no tiene plan, mostrar firma automáticamente
        if (registroEnProceso.metodo_pago === '3_cuotas' && !registroEnProceso.plan_cuotas_id) {
          setSelectedMetodoPago('3_cuotas');
          setMostrarFirmaCuotas(true);
        }
        
        // Si está en etapa de inscribir clases, cargar clases del semestre
        if (registroEnProceso.etapa_proceso === 'inscribir_clases') {
          await loadClasesSemestre(registroEnProceso.semestre_id);
        }
      }
      
      // Cargar semestres disponibles
      const semestresRes = await axios.get(`${API}/semestres-activos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // El endpoint devuelve un array plano, no {semestres: [...]}
      const semestresData = Array.isArray(semestresRes.data) ? semestresRes.data : (semestresRes.data.semestres || []);
      setSemestres(semestresData);
      
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadClasesSemestre = async (semestreId) => {
    try {
      const response = await axios.get(`${API}/semestres/${semestreId}/clases`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setClasesSemestre(response.data.clases || []);
    } catch (error) {
      console.error('Error cargando clases:', error);
    }
  };

  // PASO 1: Seleccionar método de pago
  const handleSeleccionarPago = async (semestreId) => {
    if (!selectedMetodoPago) {
      toast.error('Selecciona un método de pago');
      return;
    }

    setProcesando(true);
    try {
      const response = await axios.post(`${API}/proceso-registro/iniciar`, {
        semestre_id: semestreId,
        metodo_pago: selectedMetodoPago,
        moneda: selectedMoneda,
        // Solo se envían para grupos-seleccion (Extracurricular / Abierta / Autocapacitación)
        clases_ids: semestreSeleccionado?.clases_ids || []
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success) {
        toast.success('✅ Registro iniciado. Se generó tu invoice.');
        setRegistroActivo(response.data.registro);
        
        // Si es 3 cuotas, mostrar la firma del contrato inmediatamente
        if (selectedMetodoPago === '3_cuotas') {
          setMostrarFirmaCuotas(true);
          toast.info('📝 Firma el contrato de 3 cuotas para continuar', { duration: 5000 });
        }
        // Si es efectivo, mostrar mensaje de descargar invoice
        else if (selectedMetodoPago === 'efectivo') {
          toast.info('📄 Ve a "Mis Invoices" para descargar tu factura', { duration: 5000 });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar registro');
    } finally {
      setProcesando(false);
    }
  };

  // Firmar contrato de 3 cuotas
  const handleFirmarContratoCuotas = async (firmaBase64) => {
    if (!registroActivo?.id) {
      toast.error('No hay registro activo');
      return;
    }
    setFirmandoCuotas(true);
    try {
      const response = await axios.post(
        `${API}/planes-cuotas/crear`,
        {
          registro_id: registroActivo.id,
          firma_base64: firmaBase64
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token')}` }
        }
      );
      if (response.data.success) {
        toast.success('Contrato firmado! Recibiras una copia por email.');
        setMostrarFirmaCuotas(false);
        // Reload registration to update state
        await loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al firmar contrato');
    } finally {
      setFirmandoCuotas(false);
    }
  };

  // PASO 2: Confirmar descarga de invoice
  const handleConfirmarInvoice = async () => {
    if (!registroActivo?.id) {
      toast.error('No hay registro activo');
      return;
    }

    setProcesando(true);
    try {
      const response = await axios.post(
        `${API}/proceso-registro/${registroActivo.id}/confirmar-invoice`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (response.data.success) {
        toast.success('✅ Ahora puedes subir tu comprobante de pago');
        setRegistroActivo(response.data.registro);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar invoice');
    } finally {
      setProcesando(false);
    }
  };

  // PASO 3: Enviar recibo por WhatsApp al administrador
  const handleEnviarWhatsApp = async () => {
    if (!registroActivo?.id) {
      toast.error('No hay registro activo');
      return;
    }
    const WHATSAPP_NUMBER = '16462952037';
    const nombreCompleto = `${user?.nombre || ''} ${user?.apellido || ''}`.trim() || 'Estudiante IBA';
    const semestre = registroActivo?.semestre_nombre || '';
    const monto = registroActivo?.monto_total || registroActivo?.monto_semestre || '';
    const invoice = registroActivo?.invoice_numero ? `#${registroActivo.invoice_numero}` : '';
    const mensaje = [
      `Hola, soy *${nombreCompleto}*.`,
      '',
      'Adjunto el recibo de pago para mi registración del semestre:',
      semestre ? `🎓 Semestre: ${semestre}` : null,
      invoice ? `🧾 Invoice: ${invoice}` : null,
      monto ? `💰 Monto: ${monto}` : null,
      '',
      '📎 Adjunto el recibo en esta conversación.',
      '',
      '_Enviado desde IBA Connect._',
    ].filter(Boolean).join('\n');
    const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(mensaje)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');

    // Marcar en backend que el estudiante envió el recibo
    setUploadingComprobante(true);
    try {
      const response = await axios.post(
        `${API}/registraciones/${registroActivo.id}/marcar-enviado-whatsapp`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data?.success) {
        toast.success('✅ Recibo marcado como enviado. Ahora enviá la foto del recibo en WhatsApp.');
        await loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al marcar como enviado');
    } finally {
      setUploadingComprobante(false);
    }
  };

  // PASO 3 ALTERNATIVO: Firmar compromiso de pago (en lugar de subir comprobante)
  const handleFirmarCompromiso = async () => {
    if (!registroActivo?.id) {
      toast.error('No hay registro activo');
      return;
    }

    if (!aceptaTerminosCompromiso) {
      toast.error('Debes aceptar los términos del compromiso de pago');
      return;
    }

    setFirmandoCompromiso(true);
    try {
      const response = await axios.post(
        `${API}/proceso-registro/${registroActivo.id}/firmar-compromiso`,
        {
          acepta_terminos: true
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (response.data.success) {
        toast.success('✅ Compromiso de pago firmado exitosamente');
        setRegistroActivo(response.data.registro);
        setCompromisoPagoHabilitado(false); // Ya no tiene disponible esta opción
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al firmar compromiso');
    } finally {
      setFirmandoCompromiso(false);
    }
  };

  // PASO 3: Inscribirse a clases (automático después de validación)
  const handleInscribirClases = async () => {
    if (!registroActivo?.id) {
      toast.error('No hay registro activo');
      return;
    }

    setInscribiendoClases(true);
    try {
      const response = await axios.post(
        `${API}/proceso-registro/${registroActivo.id}/inscribir-clases`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (response.data.success) {
        toast.success('🎉 ¡Inscripción completada! Ya puedes ver tus clases.');
        setRegistroActivo(response.data.registro);
        
        // Desmarcar el flag de popup de registro para que no vuelva a redirigir
        try {
          await axios.put(`${API}/users/me/popup-registro`, 
            { mostrar_popup_registro: false },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          console.log('✅ Flag mostrar_popup_registro desmarcado al completar registro');
        } catch (err) {
          console.error('Error desmarcando flag:', err);
        }
        
        // Redirigir a Mis Clases después de 2 segundos
        setTimeout(() => {
          navigate('/app/mis-clases');
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al inscribir en clases');
    } finally {
      setInscribiendoClases(false);
    }
  };

  const getEtapaIndex = (etapaId) => {
    return ETAPAS.findIndex(e => e.id === etapaId);
  };

  // Determinar etapa actual: si no hay registro activo Y no hay semestre seleccionado, mostrar Fase 0
  const determinarEtapaActual = () => {
    if (registroActivo?.etapa_proceso) {
      return registroActivo.etapa_proceso;
    }
    // Si ya hay un semestre seleccionado (del modal o de Fase 0), ir a selección de pago
    if (semestreSeleccionado) {
      return 'seleccion_pago';
    }
    // Si no hay nada seleccionado, mostrar Fase 0
    return 'seleccion_semestre';
  };

  const etapaActual = determinarEtapaActual();
  const etapaIndex = getEtapaIndex(etapaActual);

  // Funciones para Fase 0
  const handlePreSeleccionarSemestre = (semestre) => {
    setSemestrePreSeleccionado(semestre);
    setMostrarConfirmacion(true);
  };

  const handleConfirmarSeleccion = () => {
    if (semestrePreSeleccionado) {
      // Si es grupo-selección (Extracurricular / Abierta / Autocapacitación),
      // ir al Paso 1.5 donde elige N clases individuales antes de continuar a pago.
      if (semestrePreSeleccionado.es_grupo_seleccion) {
        setMostrarSelectorClases(true);
        setMostrarConfirmacion(false);
        setClasesIdsSeleccionadas([]);
        return;
      }
      // Caso normal (Semestre completo): pasa directo a Paso 2 (pago)
      setSemestreSeleccionado(semestrePreSeleccionado);
      setMostrarConfirmacion(false);
      setSemestrePreSeleccionado(null);
      toast.success(`✅ Seleccionaste: ${semestrePreSeleccionado.nombre}`);
    }
  };

  // Toggle selección de una clase individual dentro del grupo-seleccion
  const toggleClaseSeleccionada = (claseId) => {
    setClasesIdsSeleccionadas(prev =>
      prev.includes(claseId)
        ? prev.filter(id => id !== claseId)
        : [...prev, claseId]
    );
  };

  // Confirmar selección múltiple de clases (grupo-seleccion) y pasar a Paso 2 (pago)
  const handleConfirmarClasesGrupo = () => {
    if (!semestrePreSeleccionado) return;
    if (clasesIdsSeleccionadas.length === 0) {
      toast.error('Elegí al menos una clase para continuar');
      return;
    }
    setSemestreSeleccionado({
      ...semestrePreSeleccionado,
      clases_ids: clasesIdsSeleccionadas,
      cantidad_clases: clasesIdsSeleccionadas.length
    });
    setMostrarSelectorClases(false);
    setSemestrePreSeleccionado(null);
    toast.success(`✅ ${clasesIdsSeleccionadas.length} clase(s) seleccionada(s)`);
  };

  const handleCancelarSelectorClases = () => {
    setMostrarSelectorClases(false);
    setClasesIdsSeleccionadas([]);
    setSemestrePreSeleccionado(null);
  };

  const handleCancelarSeleccion = () => {
    setMostrarConfirmacion(false);
    setSemestrePreSeleccionado(null);
  };

  const handleRetrocederEtapa = async () => {
    if (!registroActivo?.id) {
      toast.error('No hay registro activo para retroceder');
      return;
    }
    try {
      setProcesando(true);
      const response = await axios.post(
        `${API}/registraciones/${registroActivo.id}/retroceder-etapa`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token')}` } }
      );
      if (response.data.success) {
        toast.success(`Volviste a: ${response.data.etapa_nueva.replace(/_/g, ' ')}`);
        setRegistroActivo(prev => ({ ...prev, etapa_proceso: response.data.etapa_nueva }));
      }
    } catch (error) {
      console.error('Error retrocediendo etapa:', error);
      toast.error(error.response?.data?.detail || 'Error al volver al paso anterior');
    } finally {
      setProcesando(false);
    }
  };

  // Volver a Fase 0 desde Fase 1 (antes de iniciar el proceso de pago)
  const handleVolverASeleccion = () => {
    setSemestreSeleccionado(null);
  };

  if (loading || verificandoFormulario) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Si el usuario necesita llenar el formulario de datos primero
  if (necesitaFormularioDatos) {
    return (
      <div className="p-3 sm:p-6 max-w-2xl mx-auto">
        <Card className="border-2 border-amber-400 shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                <i className="fas fa-clipboard-list text-4xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">¡Un paso más antes de inscribirte!</h2>
                <p className="text-white/90 mt-1">Necesitamos algunos datos para completar tu ficha de estudiante</p>
              </div>
            </div>
          </div>
          
          <CardContent className="p-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                <i className="fas fa-info-circle"></i>
                ¿Por qué es necesario?
              </h3>
              <ul className="mt-3 space-y-2 text-blue-700">
                <li className="flex items-center gap-2">
                  <i className="fas fa-check-circle text-green-500"></i>
                  Para registrarte correctamente en el Instituto
                </li>
                <li className="flex items-center gap-2">
                  <i className="fas fa-check-circle text-green-500"></i>
                  Para emitir tu certificado de estudios
                </li>
                <li className="flex items-center gap-2">
                  <i className="fas fa-check-circle text-green-500"></i>
                  Para contactarte en caso necesario
                </li>
              </ul>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 flex items-center gap-2">
                <i className="fas fa-clock"></i>
                <span><strong>Solo toma 2-3 minutos</strong> y es un único formulario que debes llenar una sola vez.</span>
              </p>
            </div>
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  // Guardar que viene del proceso de registro para volver después
                  localStorage.setItem('volverAProcesoRegistro', 'true');
                  navigate(`/app/formulario/${FORMULARIO_DATOS_ESTUDIANTE_ID}/responder`);
                }}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-6 text-lg font-bold shadow-lg"
              >
                <i className="fas fa-edit mr-2"></i>
                Completar mis Datos Ahora
              </Button>
              
              <p className="text-center text-sm text-gray-500">
                Al completar el formulario, volverás automáticamente aquí para continuar con tu inscripción.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2 sm:gap-3">
          <i className="fas fa-clipboard-list text-purple-600"></i>
          Mi Proceso de Registro
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">Sigue los pasos para completar tu inscripción</p>
      </div>

      {/* Stepper Visual - Responsive */}
      <div className="mb-6 sm:mb-8 overflow-x-auto pb-2">
        <div className="flex items-center justify-between relative min-w-[300px]">
          {/* Línea de progreso de fondo */}
          <div className="absolute top-5 sm:top-6 left-0 right-0 h-1 bg-gray-200 z-0"></div>
          <div 
            className="absolute top-5 sm:top-6 left-0 h-1 bg-purple-600 z-0 transition-all duration-500"
            style={{ width: `${(etapaIndex / (ETAPAS.length - 1)) * 100}%` }}
          ></div>

          {ETAPAS.map((etapa, index) => {
            const isActive = index === etapaIndex;
            const isCompleted = index < etapaIndex;
            const isPending = index > etapaIndex;

            return (
              <div key={etapa.id} className="flex flex-col items-center z-10">
                <div className={`
                  w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-base sm:text-lg
                  transition-all duration-300 border-3 sm:border-4
                  ${isCompleted ? 'bg-green-500 border-green-500 text-white' : ''}
                  ${isActive ? 'bg-purple-600 border-purple-600 text-white scale-110 shadow-lg' : ''}
                  ${isPending ? 'bg-white border-gray-300 text-gray-400' : ''}
                `}>
                  {isCompleted ? (
                    <i className="fas fa-check"></i>
                  ) : (
                    <i className={`fas ${etapa.icono}`}></i>
                  )}
                </div>
                <span className={`
                  mt-1 sm:mt-2 text-[10px] sm:text-xs font-medium text-center max-w-[60px] sm:max-w-[80px]
                  ${isActive ? 'text-purple-600 font-bold' : ''}
                  ${isCompleted ? 'text-green-600' : ''}
                  ${isPending ? 'text-gray-400' : ''}
                `}>
                  {etapa.nombre}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Contenido de cada etapa */}
      <Card className="shadow-lg">
        <CardContent className="p-4 sm:p-6">
          
          {/* FASE 0: Selección de Semestre/Tipo */}
          {etapaActual === 'seleccion_semestre' && (
            <div className="space-y-6">
              {/* Logo y Bienvenida */}
              <div className="text-center mb-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_48297b72-0824-4b43-bcc7-0cce4cf7587f/artifacts/pcmnniaw_IbaLogonuevo.jpg" 
                  alt="Instituto Bíblico Apostólico del Uruguay" 
                  className="h-32 sm:h-40 mx-auto mb-4"
                />
                <h1 className="text-2xl sm:text-3xl font-bold text-indigo-900 mb-2">
                  ¡Bienvenido al Instituto Bíblico Apostólico!
                </h1>
                <p className="text-gray-600 text-lg">
                  Estás a un paso de comenzar tu formación bíblica
                </p>
              </div>

              <div className="border-t pt-6">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-list-check text-2xl text-indigo-600"></i>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">Paso 1: Selecciona tu Semestre o Tipo de Clase</h2>
                  <p className="text-gray-600">Elige la opción a la que deseas inscribirte</p>
                </div>
              </div>

              {loadingSemestres ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                </div>
              ) : semestresDisponibles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-calendar-times text-4xl mb-4"></i>
                  <p>No hay semestres o clases disponibles para registro en este momento.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/app/dashboard')}
                    className="mt-4"
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Volver al Inicio
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {semestresDisponibles.map((semestre) => {
                    // Determinar ícono y colores según tipo
                    const esGratis = !semestre.precio_uyu && !semestre.precio_usd;
                    let icono, bgColor, borderColor, hoverBorder, iconBg, iconColor;
                    
                    if (semestre.es_clase_abierta) {
                      icono = 'fa-book-open';
                      bgColor = 'bg-emerald-50';
                      borderColor = 'border-emerald-200';
                      hoverBorder = 'hover:border-emerald-400';
                      iconBg = 'bg-emerald-100';
                      iconColor = 'text-emerald-600';
                    } else if (semestre.es_ministerial) {
                      icono = 'fa-church';
                      bgColor = 'bg-violet-50';
                      borderColor = 'border-violet-200';
                      hoverBorder = 'hover:border-violet-400';
                      iconBg = 'bg-violet-100';
                      iconColor = 'text-violet-600';
                    } else {
                      icono = 'fa-graduation-cap';
                      bgColor = 'bg-sky-50';
                      borderColor = 'border-sky-200';
                      hoverBorder = 'hover:border-sky-400';
                      iconBg = 'bg-sky-100';
                      iconColor = 'text-sky-600';
                    }
                    
                    return (
                      <div 
                        key={semestre.id} 
                        className={`p-5 rounded-2xl border-2 transition-all duration-300 cursor-pointer 
                          ${bgColor} ${borderColor} ${hoverBorder}
                          hover:shadow-xl hover:scale-[1.02] active:scale-[0.99]`}
                        onClick={() => handlePreSeleccionarSemestre(semestre)}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                          {/* Ícono grande */}
                          <div className="flex items-center gap-3 sm:gap-4">
                            <div className={`w-12 h-12 sm:w-14 sm:h-14 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                              <i className={`fas ${icono} text-xl sm:text-2xl ${iconColor}`}></i>
                            </div>
                          
                            {/* Contenido */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-gray-900 text-base sm:text-lg">{semestre.nombre}</p>
                                {semestre.es_clase_abierta && (
                                  <span className="text-xs bg-emerald-200 text-emerald-800 px-2.5 py-1 rounded-full font-medium">
                                    <i className="fas fa-book-open mr-1"></i>Clase Abierta
                                  </span>
                                )}
                                {semestre.es_ministerial && (
                                  <span className="text-xs bg-violet-200 text-violet-800 px-2.5 py-1 rounded-full font-medium">
                                    <i className="fas fa-church mr-1"></i>Ministerial
                                  </span>
                                )}
                              </div>
                              {semestre.semester_number && (
                                <p className="text-sm text-gray-600 mt-1">
                                  <i className="fas fa-layer-group mr-1"></i>
                                  Semestre {semestre.semester_number}
                                </p>
                              )}
                              {(semestre.fecha_inicio || semestre.fecha_fin) && (
                                <p className="text-xs text-gray-600 mt-1 flex items-center gap-1 flex-wrap" data-testid={`fechas-semestre-${semestre.id}`}>
                                  <i className={`fas fa-calendar-alt ${iconColor}`}></i>
                                  {semestre.fecha_inicio && semestre.fecha_fin ? (
                                    <span>
                                      <span className="font-medium">{semestre.fecha_inicio}</span>
                                      <span className="mx-1 text-gray-400">→</span>
                                      <span className="font-medium">{semestre.fecha_fin}</span>
                                    </span>
                                  ) : semestre.fecha_inicio ? (
                                    <span>Inicia <span className="font-medium">{semestre.fecha_inicio}</span></span>
                                  ) : (
                                    <span>Finaliza <span className="font-medium">{semestre.fecha_fin}</span></span>
                                  )}
                                </p>
                              )}
                              {semestre.descripcion && (
                                <p
                                  className="text-xs text-gray-600 mt-1.5 leading-snug break-words italic"
                                  data-testid={`descripcion-semestre-${semestre.id}`}
                                >
                                  <i className={`fas fa-info-circle mr-1 ${iconColor}`}></i>
                                  {semestre.descripcion}
                                </p>
                              )}
                              {/* Badge diferenciador: "Elegí clases" vs "Paquete completo" */}
                              {semestre.es_grupo_seleccion ? (
                                <p
                                  className="text-[10px] font-bold uppercase tracking-wide mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200"
                                  data-testid={`badge-grupo-seleccion-${semestre.id}`}
                                >
                                  <i className="fas fa-check-double"></i>
                                  Elegí 1 o más clases · Precio por clase
                                </p>
                              ) : (
                                <p
                                  className="text-[10px] font-bold uppercase tracking-wide mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200"
                                  data-testid={`badge-paquete-${semestre.id}`}
                                >
                                  <i className="fas fa-box-open"></i>
                                  Paquete completo · Incluye todas
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Precio y flecha */}
                          <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                            {esGratis ? (
                              <div className="bg-green-500 text-white px-4 py-2 rounded-full font-bold text-sm flex items-center gap-1 shadow-md">
                                <i className="fas fa-check-circle"></i>
                                GRATIS
                              </div>
                            ) : (
                              <div className="text-right">
                                <div className="bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg">
                                  <p className="text-xs font-medium flex items-center gap-1">
                                    <i className="fas fa-tag"></i> Precio
                                  </p>
                                  <p className="font-bold text-sm">
                                    ${semestre.precio_uyu} <span className="text-indigo-400">UYU</span>
                                  </p>
                                  <p className="text-xs text-indigo-500">
                                    ${semestre.precio_usd} USD
                                  </p>
                                </div>
                              </div>
                            )}
                            <div className={`w-10 h-10 ${iconBg} rounded-full flex items-center justify-center`}>
                              <i className={`fas fa-arrow-right ${iconColor}`}></i>
                            </div>
                          </div>
                        </div>

                        {/* Lista de clases incluidas (visible siempre) */}
                        {Array.isArray(semestre.clases_incluidas) && semestre.clases_incluidas.length > 0 && (
                          <div
                            className={`mt-4 pt-4 border-t ${borderColor}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${iconColor}`}>
                              <i className="fas fa-list-ul mr-1.5" />
                              {semestre.clases_incluidas.length === 1
                                ? '1 clase incluida'
                                : `${semestre.clases_incluidas.length} clases incluidas`}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {semestre.clases_incluidas.map((cl, idx) => (
                                <div
                                  key={idx}
                                  className="bg-white/70 rounded-lg px-3 py-2 shadow-sm border border-white flex items-start gap-2"
                                  data-testid={`clase-incluida-${semestre.id}-${idx}`}
                                >
                                  {cl.imagen_url ? (
                                    <img
                                      src={cl.imagen_url}
                                      alt={cl.nombre}
                                      loading="lazy"
                                      className="w-10 h-10 rounded-md object-cover flex-shrink-0 mt-0.5 border border-white shadow-sm"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        const fallback = e.currentTarget.nextElementSibling;
                                        if (fallback) fallback.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    className={`w-10 h-10 ${iconBg} rounded-md items-center justify-center flex-shrink-0 mt-0.5 border border-white shadow-sm ${cl.imagen_url ? 'hidden' : 'flex'}`}
                                    style={cl.imagen_url ? { display: 'none' } : undefined}
                                  >
                                    <i className={`fas fa-book text-sm ${iconColor}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 leading-tight break-words">
                                      {cl.nombre}
                                    </p>
                                    {cl.profesor_nombre && (
                                      <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                                        <i className="fas fa-user-tie text-[9px]" />
                                        <span className="truncate">{cl.profesor_nombre}</span>
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Tip informativo */}
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mt-6">
                <p className="text-sm text-yellow-800 flex items-start gap-2">
                  <i className="fas fa-info-circle mt-1"></i>
                  <span>
                    <strong>Tip:</strong> Haz clic en la opción que deseas para continuar con el proceso de inscripción. 
                    Se te pedirá confirmación antes de continuar.
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Modal de Confirmación de Selección */}
          {mostrarConfirmacion && semestrePreSeleccionado && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-fade-in">
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-clipboard-check text-4xl text-amber-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">¿Confirmar selección?</h3>
                    <p className="text-gray-500 mt-1">Verifica que sea la opción correcta</p>
                  </div>
                  
                  {(() => {
                    // Determinar estilos según tipo
                    const esGratis = !semestrePreSeleccionado.precio_uyu && !semestrePreSeleccionado.precio_usd;
                    let icono, bgColor, borderColor, iconBg, iconColor;
                    
                    if (semestrePreSeleccionado.es_clase_abierta) {
                      icono = 'fa-book-open';
                      bgColor = 'bg-emerald-50';
                      borderColor = 'border-emerald-300';
                      iconBg = 'bg-emerald-100';
                      iconColor = 'text-emerald-600';
                    } else if (semestrePreSeleccionado.es_ministerial) {
                      icono = 'fa-church';
                      bgColor = 'bg-violet-50';
                      borderColor = 'border-violet-300';
                      iconBg = 'bg-violet-100';
                      iconColor = 'text-violet-600';
                    } else {
                      icono = 'fa-graduation-cap';
                      bgColor = 'bg-sky-50';
                      borderColor = 'border-sky-300';
                      iconBg = 'bg-sky-100';
                      iconColor = 'text-sky-600';
                    }
                    
                    return (
                      <div className={`p-5 rounded-2xl border-2 mb-6 ${bgColor} ${borderColor}`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <i className={`fas ${icono} text-2xl ${iconColor}`}></i>
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-gray-900 text-lg">{semestrePreSeleccionado.nombre}</p>
                            {semestrePreSeleccionado.semester_number && (
                              <p className="text-sm text-gray-600">
                                <i className="fas fa-layer-group mr-1"></i>
                                Semestre {semestrePreSeleccionado.semester_number}
                              </p>
                            )}
                            <div className="mt-2">
                              {esGratis ? (
                                <span className="inline-flex items-center gap-1 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                                  <i className="fas fa-check-circle"></i> GRATIS
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-lg text-sm font-semibold">
                                  <i className="fas fa-tag"></i>
                                  ${semestrePreSeleccionado.precio_uyu} UYU / ${semestrePreSeleccionado.precio_usd} USD
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <p className="text-gray-600 text-center mb-6">
                    ¿Estás seguro de que deseas inscribirte a esta opción? 
                    <br />
                    <span className="text-sm text-gray-500">Podrás cambiar antes de realizar el pago.</span>
                  </p>

                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={handleCancelarSeleccion}
                      className="flex-1"
                    >
                      <i className="fas fa-arrow-left mr-2"></i>
                      Volver
                    </Button>
                    <Button 
                      onClick={handleConfirmarSeleccion}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      <i className="fas fa-check mr-2"></i>
                      Sí, Continuar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modal Paso 1.5: Selección múltiple de clases para grupos-seleccion */}
          {mostrarSelectorClases && semestrePreSeleccionado && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4" data-testid="modal-selector-clases">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 text-white">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <i className="fas fa-check-double"></i>
                    Elegí tus clases · {semestrePreSeleccionado.nombre}
                  </h3>
                  <p className="text-sm text-white/90 mt-1">
                    Precio por clase: <span className="font-bold">${semestrePreSeleccionado.precio_uyu} UYU</span> / <span className="font-bold">${semestrePreSeleccionado.precio_usd} USD</span>. El total se calcula por la cantidad de clases seleccionadas.
                  </p>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {(!semestrePreSeleccionado.clases_incluidas || semestrePreSeleccionado.clases_incluidas.length === 0) ? (
                    <div className="text-center py-8 text-gray-500">
                      <i className="fas fa-inbox text-3xl mb-2"></i>
                      <p>No hay clases disponibles en este grupo.</p>
                      <p className="text-xs">O ya estás inscrito en todas las clases de este grupo.</p>
                    </div>
                  ) : (
                    semestrePreSeleccionado.clases_incluidas.map((cl) => {
                      const seleccionada = clasesIdsSeleccionadas.includes(cl.id);
                      return (
                        <label
                          key={cl.id}
                          data-testid={`seleccion-clase-${cl.id}`}
                          className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${seleccionada ? 'border-emerald-500 bg-emerald-50 shadow-md' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
                        >
                          <input
                            type="checkbox"
                            checked={seleccionada}
                            onChange={() => toggleClaseSeleccionada(cl.id)}
                            className="mt-1 w-5 h-5 accent-emerald-600"
                          />
                          {cl.imagen_url ? (
                            <img src={cl.imagen_url} alt={cl.nombre} className="w-14 h-14 rounded-lg object-cover border shadow-sm" loading="lazy" />
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-emerald-100 flex items-center justify-center">
                              <i className="fas fa-book text-emerald-700 text-lg"></i>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-800 break-words">{cl.nombre}</p>
                            {cl.profesor_nombre && (
                              <p className="text-xs text-gray-600 mt-0.5 flex items-center gap-1">
                                <i className="fas fa-user-tie"></i>
                                {cl.profesor_nombre}
                              </p>
                            )}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>

                <div className="border-t bg-gray-50 p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="text-sm text-gray-700" data-testid="total-clases-grupo">
                    <span className="font-semibold">{clasesIdsSeleccionadas.length}</span> clase(s) seleccionada(s) · Total:{' '}
                    <span className="font-bold text-emerald-700">
                      ${((semestrePreSeleccionado.precio_uyu || 0) * clasesIdsSeleccionadas.length).toLocaleString()} UYU
                    </span>{' '}
                    /{' '}
                    <span className="font-bold text-emerald-700">
                      ${((semestrePreSeleccionado.precio_usd || 0) * clasesIdsSeleccionadas.length).toLocaleString()} USD
                    </span>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Button variant="outline" onClick={handleCancelarSelectorClases} data-testid="cancelar-selector-clases" className="flex-1 sm:flex-none">
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleConfirmarClasesGrupo}
                      disabled={clasesIdsSeleccionadas.length === 0}
                      data-testid="confirmar-selector-clases"
                      className="flex-1 sm:flex-none bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                    >
                      Continuar con {clasesIdsSeleccionadas.length || 0} clase(s)
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ETAPA 1: Selección de Pago */}
          {etapaActual === 'seleccion_pago' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-credit-card text-3xl text-blue-600"></i>
                </div>
                <h2 className="text-xl font-bold text-gray-800">Paso 2: Selecciona tu forma de pago</h2>
                <p className="text-gray-600">Elige cómo deseas pagar tu matrícula</p>
              </div>

              {/* Botón para volver a seleccionar semestre */}
              {!registroActivo && (
                <div className="mb-4">
                  <Button 
                    variant="ghost" 
                    onClick={handleVolverASeleccion}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Cambiar selección de semestre
                  </Button>
                </div>
              )}
              {semestreSeleccionado ? (
                <div className="border rounded-xl p-6 bg-gray-50">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-bold text-lg">{semestreSeleccionado.name || semestreSeleccionado.nombre}</h3>
                      <p className="text-gray-600 text-sm">{semestreSeleccionado.descripcion || semestreSeleccionado.description}</p>
                      {semestreSeleccionado.cantidad_clases > 0 && (
                        <p className="text-xs text-emerald-700 font-semibold mt-1" data-testid="cantidad-clases-grupo">
                          <i className="fas fa-check-double mr-1"></i>
                          {semestreSeleccionado.cantidad_clases} clase(s) seleccionada(s)
                        </p>
                      )}
                    </div>
                    <Badge className="bg-purple-100 text-purple-700" data-testid="badge-precio-total">
                      ${((semestreSeleccionado.price_uyu || semestreSeleccionado.precio_uyu || 0) * (semestreSeleccionado.cantidad_clases || 1)).toLocaleString()} UYU / ${((semestreSeleccionado.price_usd || semestreSeleccionado.precio_usd || 0) * (semestreSeleccionado.cantidad_clases || 1)).toLocaleString()} USD
                    </Badge>
                  </div>

                  {/* Selector de moneda */}
                  <div className="mb-4">
                    <Label className="text-sm font-medium mb-2 block">Moneda:</Label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setSelectedMoneda('UYU')}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          selectedMoneda === 'UYU'
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        🇺🇾 Pesos (UYU)
                      </button>
                      <button
                        onClick={() => setSelectedMoneda('USD')}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          selectedMoneda === 'USD'
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        🇺🇸 Dólares (USD)
                      </button>
                    </div>
                  </div>

                  {/* Métodos de pago */}
                  <div className="mb-4">
                    <Label className="text-sm font-medium mb-2 block">Método de pago:</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Efectivo */}
                      <button
                        type="button"
                        onClick={() => setSelectedMetodoPago('efectivo')}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          selectedMetodoPago === 'efectivo'
                            ? 'border-green-500 bg-green-50 shadow-lg'
                            : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                        }`}
                      >
                        <div className="flex flex-col items-center text-center gap-2">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            selectedMetodoPago === 'efectivo' ? 'bg-green-500' : 'bg-green-100'
                          }`}>
                            <i className={`fas fa-money-bill-wave text-xl ${
                              selectedMetodoPago === 'efectivo' ? 'text-white' : 'text-green-600'
                            }`}></i>
                          </div>
                          <div>
                            <div className="font-bold text-gray-800 text-sm">Efectivo</div>
                            <div className="text-xs text-gray-500">Abitab</div>
                          </div>
                          {selectedMetodoPago === 'efectivo' && (
                            <i className="fas fa-check-circle text-green-500"></i>
                          )}
                        </div>
                      </button>

                      {/* Banco/Transferencia */}
                      <button
                        type="button"
                        onClick={() => setSelectedMetodoPago('transferencia')}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          selectedMetodoPago === 'transferencia'
                            ? 'border-orange-500 bg-orange-50 shadow-lg'
                            : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50'
                        }`}
                      >
                        <div className="flex flex-col items-center text-center gap-2">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            selectedMetodoPago === 'transferencia' ? 'bg-orange-500' : 'bg-orange-100'
                          }`}>
                            <i className={`fas fa-university text-xl ${
                              selectedMetodoPago === 'transferencia' ? 'text-white' : 'text-orange-600'
                            }`}></i>
                          </div>
                          <div>
                            <div className="font-bold text-gray-800 text-sm">Banco</div>
                            <div className="text-xs text-gray-500">Transferencia</div>
                          </div>
                          {selectedMetodoPago === 'transferencia' && (
                            <i className="fas fa-check-circle text-orange-500"></i>
                          )}
                        </div>
                      </button>

                      {/* Becado */}
                      <button
                        type="button"
                        onClick={() => setSelectedMetodoPago('becado')}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          selectedMetodoPago === 'becado'
                            ? 'border-purple-500 bg-purple-50 shadow-lg'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                        }`}
                      >
                        <div className="flex flex-col items-center text-center gap-2">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            selectedMetodoPago === 'becado' ? 'bg-purple-500' : 'bg-purple-100'
                          }`}>
                            <i className={`fas fa-graduation-cap text-xl ${
                              selectedMetodoPago === 'becado' ? 'text-white' : 'text-purple-600'
                            }`}></i>
                          </div>
                          <div>
                            <div className="font-bold text-gray-800 text-sm">Becado</div>
                            <div className="text-xs text-gray-500">Monto $0</div>
                          </div>
                          {selectedMetodoPago === 'becado' && (
                            <i className="fas fa-check-circle text-purple-500"></i>
                          )}
                        </div>
                      </button>

                      {/* Compromiso de Pago - Solo si está habilitado */}
                      {compromisoPagoHabilitado && (
                        <button
                          type="button"
                          onClick={() => setSelectedMetodoPago('compromiso')}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                            selectedMetodoPago === 'compromiso'
                              ? 'border-amber-500 bg-amber-50 shadow-lg'
                              : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                          }`}
                        >
                          <div className="flex flex-col items-center text-center gap-2">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              selectedMetodoPago === 'compromiso' ? 'bg-amber-500' : 'bg-amber-100'
                            }`}>
                              <i className={`fas fa-handshake text-xl ${
                                selectedMetodoPago === 'compromiso' ? 'text-white' : 'text-amber-600'
                              }`}></i>
                            </div>
                            <div>
                              <div className="font-bold text-gray-800 text-sm">Compromiso</div>
                              <div className="text-xs text-gray-500">Plazo {compromisoPagoPlazo} días</div>
                            </div>
                            {selectedMetodoPago === 'compromiso' && (
                              <i className="fas fa-check-circle text-amber-500"></i>
                            )}
                          </div>
                        </button>
                      )}
                      {pagoCuotas3Habilitado && (
                        <button
                          type="button"
                          onClick={() => setSelectedMetodoPago('3_cuotas')}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                            selectedMetodoPago === '3_cuotas'
                              ? 'border-indigo-500 bg-indigo-50 shadow-lg'
                              : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50'
                          }`}
                          data-testid="metodo-pago-3-cuotas"
                        >
                          <div className="flex flex-col items-center text-center gap-2">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              selectedMetodoPago === '3_cuotas' ? 'bg-indigo-500' : 'bg-indigo-100'
                            }`}>
                              <i className={`fas fa-file-contract text-xl ${
                                selectedMetodoPago === '3_cuotas' ? 'text-white' : 'text-indigo-600'
                              }`}></i>
                            </div>
                            <div>
                              <div className="font-bold text-gray-800 text-sm">3 Cuotas</div>
                              <div className="text-xs text-gray-500">Pago fraccionado</div>
                            </div>
                            {selectedMetodoPago === '3_cuotas' && (
                              <i className="fas fa-check-circle text-indigo-500"></i>
                            )}
                          </div>
                        </button>
                      )}
                    </div>

                    {/* Nota explicativa si selecciona Compromiso */}
                    {selectedMetodoPago === 'compromiso' && (
                      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <i className="fas fa-info-circle text-amber-500 mt-0.5"></i>
                          <div className="text-sm text-amber-800">
                            <p className="font-semibold mb-1">Compromiso de Pago</p>
                            <p>Tienes <strong>{compromisoPagoPlazo} días</strong> para realizar el pago después de la inscripción. Si no subes el comprobante de pago antes de la fecha límite, tu acceso a la plataforma será bloqueado temporalmente.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Nota explicativa si selecciona 3 Cuotas */}
                    {selectedMetodoPago === '3_cuotas' && (
                      <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="nota-3-cuotas">
                        <div className="flex items-start gap-3">
                          <i className="fas fa-info-circle text-indigo-500 mt-0.5"></i>
                          <div className="text-sm text-indigo-800">
                            <p className="font-semibold mb-1">Pago en 3 Cuotas</p>
                            <p>El monto total se dividira en <strong>3 cuotas iguales</strong>. Deberas firmar un contrato digital y subir comprobante de pago para cada cuota:</p>
                            <ul className="list-disc ml-4 mt-2 space-y-1">
                              <li><strong>Cuota 1:</strong> Vence 1 semana despues del registro</li>
                              <li><strong>Cuota 2:</strong> Vence 1 mes despues</li>
                              <li><strong>Cuota 3:</strong> Vence 2 meses despues</li>
                            </ul>
                            <p className="mt-2 text-red-700 font-medium">Si no subes el comprobante dentro de los 2 dias posteriores al vencimiento, tu acceso sera bloqueado.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Firma de Contrato para 3 Cuotas */}
                  {selectedMetodoPago === '3_cuotas' && mostrarFirmaCuotas && registroActivo && (
                    <div className="mt-6 p-5 bg-indigo-50 border border-indigo-200 rounded-xl" data-testid="firma-cuotas-section">
                      <h4 className="font-bold text-indigo-900 mb-3 flex items-center gap-2">
                        <i className="fas fa-file-signature" />
                        Firma el Contrato de 3 Cuotas
                      </h4>
                      <p className="text-sm text-indigo-700 mb-4">
                        Al firmar aceptas el compromiso de pagar en 3 cuotas iguales segun las fechas establecidas en el contrato. 
                        Recibiras una copia del contrato firmado por email.
                      </p>
                      <FirmaDigital 
                        onFirmar={handleFirmarContratoCuotas}
                      />
                      {firmandoCuotas && (
                        <div className="mt-3 flex items-center gap-2 text-indigo-600">
                          <i className="fas fa-spinner fa-spin" />
                          <span className="text-sm">Generando contrato y enviando por email...</span>
                        </div>
                      )}
                    </div>
                  )}

                  <Button 
                    onClick={() => {
                      if (selectedMetodoPago === '3_cuotas') {
                        if (!registroActivo) {
                          handleSeleccionarPago(semestreSeleccionado.id);
                        } else {
                          setMostrarFirmaCuotas(true);
                        }
                      } else {
                        handleSeleccionarPago(semestreSeleccionado.id);
                      }
                    }}
                    disabled={procesando || firmandoCuotas || (selectedMetodoPago === '3_cuotas' && mostrarFirmaCuotas)}
                    className="w-full bg-purple-600 hover:bg-purple-700 py-3"
                  >
                    {procesando ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Procesando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-arrow-right mr-2"></i>
                        Continuar al Paso 3
                      </>
                    )}
                  </Button>
                </div>
              ) : semestres.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-calendar-times text-4xl mb-4"></i>
                  <p>No hay semestres disponibles para registro en este momento.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/app/dashboard')}
                    className="mt-4"
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Volver al Inicio
                  </Button>
                </div>
              ) : (
                semestres.map(semestre => (
                  <div key={semestre.id} className="border rounded-xl p-6 bg-gray-50">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-bold text-lg">{semestre.nombre}</h3>
                        <p className="text-gray-600 text-sm">{semestre.descripcion}</p>
                      </div>
                      <Badge className="bg-purple-100 text-purple-700">
                        ${semestre.precio_uyu} UYU / ${semestre.precio_usd} USD
                      </Badge>
                    </div>

                    {/* Selector de moneda */}
                    <div className="mb-4">
                      <Label className="text-sm font-medium mb-2 block">Moneda:</Label>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setSelectedMoneda('UYU')}
                          className={`px-4 py-2 rounded-lg border-2 transition-all ${
                            selectedMoneda === 'UYU'
                              ? 'border-purple-500 bg-purple-50 text-purple-700'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          🇺🇾 Pesos (UYU)
                        </button>
                        <button
                          onClick={() => setSelectedMoneda('USD')}
                          className={`px-4 py-2 rounded-lg border-2 transition-all ${
                            selectedMoneda === 'USD'
                              ? 'border-purple-500 bg-purple-50 text-purple-700'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          🇺🇸 Dólares (USD)
                        </button>
                      </div>
                    </div>

                    {/* Métodos de pago */}
                    <div className="mb-4">
                      <Label className="text-sm font-medium mb-2 block">Método de pago:</Label>
                      <div className="grid grid-cols-3 gap-3">
                        {/* Efectivo */}
                        <button
                          type="button"
                          onClick={() => setSelectedMetodoPago('efectivo')}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                            selectedMetodoPago === 'efectivo'
                              ? 'border-green-500 bg-green-50 shadow-lg'
                              : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                          }`}
                        >
                          <div className="flex flex-col items-center text-center gap-2">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              selectedMetodoPago === 'efectivo' ? 'bg-green-500' : 'bg-green-100'
                            }`}>
                              <i className={`fas fa-money-bill-wave text-xl ${
                                selectedMetodoPago === 'efectivo' ? 'text-white' : 'text-green-600'
                              }`}></i>
                            </div>
                            <div>
                              <div className="font-bold text-gray-800 text-sm">Efectivo</div>
                              <div className="text-xs text-gray-500">Abitab</div>
                            </div>
                            {selectedMetodoPago === 'efectivo' && (
                              <i className="fas fa-check-circle text-green-500"></i>
                            )}
                          </div>
                        </button>

                        {/* Banco/Transferencia */}
                        <button
                          type="button"
                          onClick={() => setSelectedMetodoPago('transferencia')}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                            selectedMetodoPago === 'transferencia'
                              ? 'border-orange-500 bg-orange-50 shadow-lg'
                              : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50'
                          }`}
                        >
                          <div className="flex flex-col items-center text-center gap-2">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              selectedMetodoPago === 'transferencia' ? 'bg-orange-500' : 'bg-orange-100'
                            }`}>
                              <i className={`fas fa-university text-xl ${
                                selectedMetodoPago === 'transferencia' ? 'text-white' : 'text-orange-600'
                              }`}></i>
                            </div>
                            <div>
                              <div className="font-bold text-gray-800 text-sm">Banco</div>
                              <div className="text-xs text-gray-500">Transferencia</div>
                            </div>
                            {selectedMetodoPago === 'transferencia' && (
                              <i className="fas fa-check-circle text-orange-500"></i>
                            )}
                          </div>
                        </button>

                        {/* Becado */}
                        <button
                          type="button"
                          onClick={() => setSelectedMetodoPago('becado')}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                            selectedMetodoPago === 'becado'
                              ? 'border-purple-500 bg-purple-50 shadow-lg'
                              : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                          }`}
                        >
                          <div className="flex flex-col items-center text-center gap-2">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              selectedMetodoPago === 'becado' ? 'bg-purple-500' : 'bg-purple-100'
                            }`}>
                              <i className={`fas fa-graduation-cap text-xl ${
                                selectedMetodoPago === 'becado' ? 'text-white' : 'text-purple-600'
                              }`}></i>
                            </div>
                            <div>
                              <div className="font-bold text-gray-800 text-sm">Becado</div>
                              <div className="text-xs text-gray-500">Monto $0</div>
                            </div>
                            {selectedMetodoPago === 'becado' && (
                              <i className="fas fa-check-circle text-purple-500"></i>
                            )}
                          </div>
                        </button>
                      </div>
                    </div>

                    <Button 
                      onClick={() => handleSeleccionarPago(semestre.id)}
                      disabled={procesando}
                      className="w-full bg-purple-600 hover:bg-purple-700 py-3"
                    >
                      {procesando ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Procesando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-arrow-right mr-2"></i>
                          Continuar al Paso 3
                        </>
                      )}
                    </Button>
                    {registroActivo?.id && (
                    <Button 
                      variant="outline"
                      onClick={handleRetrocederEtapa}
                      disabled={procesando}
                      className="w-full mt-2 text-gray-600 hover:text-gray-800"
                      data-testid="volver-paso-anterior-pago-btn"
                    >
                      <i className="fas fa-arrow-left mr-2"></i>
                      Volver al paso anterior
                    </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* ETAPA 2: Descargar Invoice */}
          {/* ETAPA 2.5: Firma de Contrato 3 Cuotas (se muestra antes de continuar) */}
          {registroActivo?.metodo_pago === '3_cuotas' && !registroActivo?.plan_cuotas_id && mostrarFirmaCuotas && (
            <div className="space-y-6" data-testid="firma-cuotas-step">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-file-signature text-3xl text-indigo-600"></i>
                </div>
                <h2 className="text-xl font-bold text-gray-800">Firma el Contrato de 3 Cuotas</h2>
                <p className="text-gray-600">Lee los terminos y firma digitalmente para continuar</p>
              </div>

              {/* Info del plan */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <i className="fas fa-info-circle text-indigo-600 mt-1"></i>
                  <div>
                    <p className="font-medium text-indigo-800">Detalles del plan de cuotas:</p>
                    <ul className="text-sm text-indigo-700 mt-2 space-y-1">
                      <li>Semestre: <strong>{registroActivo.semestre_nombre}</strong></li>
                      <li>Monto total: <strong>{registroActivo.moneda} {(registroActivo.monto_pagado || registroActivo.monto)?.toLocaleString()}</strong></li>
                      <li>3 cuotas de: <strong>{registroActivo.moneda} {Math.round((registroActivo.monto_pagado || registroActivo.monto) / 3)?.toLocaleString()}</strong></li>
                      <li>Cuota 1: <strong>1 semana despues del registro</strong></li>
                      <li>Cuota 2: <strong>1 mes despues</strong></li>
                      <li>Cuota 3: <strong>2 meses despues</strong></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Terminos */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-700 space-y-2 max-h-48 overflow-y-auto">
                <p className="font-bold">Terminos del contrato:</p>
                <p>1. Me comprometo a pagar el monto total dividido en 3 cuotas iguales.</p>
                <p>2. Cada cuota debe ser abonada antes de su fecha de vencimiento con un plazo de gracia de 2 dias.</p>
                <p>3. Debo subir el comprobante de pago en la seccion "Mis Cuotas" del Area Financiera.</p>
                <p>4. Si no subo el comprobante dentro del plazo, mi acceso a la plataforma sera bloqueado temporalmente.</p>
                <p>5. El acceso se restablecera una vez que suba el comprobante pendiente.</p>
              </div>

              {/* Firma */}
              <div className="bg-white border border-indigo-200 rounded-xl p-4">
                <FirmaDigital onFirmar={handleFirmarContratoCuotas} />
              </div>

              {firmandoCuotas && (
                <div className="flex items-center justify-center gap-3 text-indigo-600 py-4">
                  <i className="fas fa-spinner fa-spin text-xl" />
                  <span>Generando contrato y enviando por email...</span>
                </div>
              )}
            </div>
          )}

          {/* ETAPA 2: Descargar Invoice (solo si NO está pendiente la firma de cuotas) */}
          {etapaActual === 'descargar_invoice' && !(registroActivo?.metodo_pago === '3_cuotas' && !registroActivo?.plan_cuotas_id && mostrarFirmaCuotas) && (
            <DescargarInvoiceStep 
              registroActivo={registroActivo}
              API={API}
              handleConfirmarInvoice={handleConfirmarInvoice}
              handleRetrocederEtapa={handleRetrocederEtapa}
              procesando={procesando}
            />
          )}

          {/* ETAPA 3: Subir Comprobante o Firmar Compromiso */}
          {etapaActual === 'esperando_comprobante' && (
            <div className="space-y-6">
              {/* Si es método compromiso, mostrar firma de compromiso */}
              {registroActivo?.metodo_pago === 'compromiso' ? (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-handshake text-3xl text-amber-600"></i>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800">Paso 4: Firma tu Compromiso de Pago</h2>
                    <p className="text-gray-600">Lee y acepta los términos para completar tu inscripción</p>
                  </div>

                  {/* Info del registro */}
                  {registroActivo && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <i className="fas fa-info-circle text-amber-600 mt-1"></i>
                        <div>
                          <p className="font-medium text-amber-800">Información de tu compromiso:</p>
                          <ul className="text-sm text-amber-700 mt-2 space-y-1">
                            <li>• Semestre: <strong>{registroActivo.semestre_nombre || registroActivo.semestre}</strong></li>
                            <li>• Monto a pagar: <strong>${registroActivo.monto} {registroActivo.moneda}</strong></li>
                            <li>• Plazo para pagar: <strong>{compromisoPagoPlazo} días</strong></li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botón para descargar invoice */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3">
                        <i className="fas fa-file-invoice text-yellow-600 mt-1"></i>
                        <div>
                          <p className="font-medium text-yellow-800">Descarga tu factura</p>
                          <p className="text-sm text-yellow-700">Guárdala para realizar el pago</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => navigate('/app/mis-invoices')}
                        className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
                      >
                        <i className="fas fa-download mr-2"></i>
                        Ir a Mis Invoices
                      </Button>
                    </div>
                  </div>

                  {/* Documento de Compromiso de Pago - Estilo Oficial */}
                  <div className="bg-white border-2 border-gray-300 rounded-xl overflow-hidden shadow-lg">
                    {/* Encabezado con Logo */}
                    <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6 text-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_stability-boost-7/artifacts/r0xf23t0_IbaLogonuevo.jpg" 
                        alt="IBA Uruguay" 
                        className="w-20 h-20 mx-auto rounded-full border-4 border-white shadow-lg mb-3"
                      />
                      <h2 className="text-white text-xl font-bold">Instituto Bíblico Apostólico del Uruguay</h2>
                      <p className="text-blue-200 text-sm mt-1">Formando líderes para el Reino</p>
                    </div>

                    {/* Título del Documento */}
                    <div className="bg-amber-50 border-b-2 border-amber-200 p-4 text-center">
                      <h3 className="text-xl font-bold text-amber-800 flex items-center justify-center gap-2">
                        <i className="fas fa-file-signature"></i>
                        COMPROMISO DE PAGO
                      </h3>
                      <p className="text-amber-600 text-sm mt-1">Documento de Acuerdo de Pago Diferido</p>
                    </div>

                    {/* Cuerpo del Documento */}
                    <div className="p-6">
                      {/* Fecha */}
                      <p className="text-right text-gray-500 text-sm mb-4">
                        Fecha: {new Date().toLocaleDateString('es-UY', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </p>

                      {/* Contenido del Compromiso */}
                      <div className="prose prose-sm text-gray-700 bg-gray-50 p-5 rounded-lg border border-gray-200 mb-6">
                        <p className="text-justify leading-relaxed">
                          Yo, <strong className="text-gray-900">{user?.nombre} {user?.apellido}</strong>, 
                          identificado/a con el correo electrónico <strong className="text-gray-900">{user?.email}</strong>, 
                          por medio del presente documento me comprometo formalmente a realizar el pago de la matrícula 
                          correspondiente al semestre <strong className="text-blue-800">{registroActivo?.semestre_nombre}</strong> por 
                          un monto total de <strong className="text-green-700">${registroActivo?.monto} {registroActivo?.moneda}</strong> dentro 
                          del plazo de <strong className="text-amber-700">{compromisoPagoPlazo} días</strong> a partir del <strong className="text-blue-700">inicio de clases (9 de marzo de 2026)</strong>.
                        </p>
                        
                        <p className="mt-4 font-semibold text-gray-800">Declaro conocer y aceptar las siguientes condiciones:</p>
                        
                        <ol className="list-decimal pl-5 space-y-2 mt-3 text-gray-700">
                          <li>Me comprometo a subir el comprobante de pago a través de la plataforma IBA Connect antes de la fecha límite establecida.</li>
                          <li>Entiendo que si no cumplo con el pago dentro del plazo acordado, mi acceso a la plataforma será <strong className="text-red-600">suspendido temporalmente</strong> hasta que regularice mi situación financiera.</li>
                          <li>Me comprometo a contactar al administrador del instituto con anticipación si preveo dificultades para cumplir con el plazo establecido.</li>
                          <li>Reconozco que este compromiso es un acuerdo de buena fe entre el estudiante y el Instituto Bíblico Apostólico del Uruguay.</li>
                        </ol>

                        <div className="mt-5 p-3 bg-blue-100 border-l-4 border-blue-500 rounded">
                          <p className="text-sm text-blue-800">
                            <i className="fas fa-school mr-2"></i>
                            <strong>Inicio de clases:</strong> 9 de marzo de 2026
                          </p>
                        </div>

                        <div className="mt-3 p-3 bg-amber-100 border-l-4 border-amber-500 rounded">
                          <p className="text-sm text-amber-800">
                            <i className="fas fa-calendar-alt mr-2"></i>
                            <strong>Fecha límite de pago:</strong> {new Date(new Date('2026-03-09').getTime() + compromisoPagoPlazo * 24 * 60 * 60 * 1000).toLocaleDateString('es-UY', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                            <span className="ml-2 text-amber-600">({compromisoPagoPlazo} días después del inicio de clases)</span>
                          </p>
                        </div>
                      </div>

                      {/* Checkbox de aceptación */}
                      <label className="flex items-start gap-3 cursor-pointer p-4 bg-amber-50 rounded-lg border-2 border-amber-300 hover:bg-amber-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={aceptaTerminosCompromiso}
                          onChange={(e) => setAceptaTerminosCompromiso(e.target.checked)}
                          className="w-6 h-6 rounded border-amber-400 text-amber-600 focus:ring-amber-500 mt-0.5"
                        />
                        <span className="text-amber-900">
                          <strong>Acepto los términos y condiciones del presente Compromiso de Pago</strong> y declaro que la información proporcionada es verídica. Entiendo las consecuencias del incumplimiento de este acuerdo.
                        </span>
                      </label>

                      {/* Botón de firma */}
                      <Button
                        onClick={handleFirmarCompromiso}
                        disabled={!aceptaTerminosCompromiso || firmandoCompromiso}
                        className="w-full mt-6 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 py-4 text-lg font-bold shadow-lg"
                      >
                        {firmandoCompromiso ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            Procesando firma digital...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-signature mr-2"></i>
                            Firmar Compromiso Digitalmente
                          </>
                        )}
                      </Button>

                      {/* Nota al pie */}
                      <p className="text-xs text-gray-400 text-center mt-4">
                        Al firmar este documento, se registrará la fecha, hora y su identificación como constancia digital del acuerdo.
                      </p>
                    </div>
                  </div>

                  {/* Botón de retroceso */}
                  <Button 
                    variant="outline"
                    onClick={handleRetrocederEtapa}
                    disabled={procesando}
                    className="w-full mt-4 text-gray-600 hover:text-gray-800"
                    data-testid="volver-paso-anterior-compromiso-btn"
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Volver al paso anterior
                  </Button>
                </>
              ) : (
                /* Si NO es compromiso, mostrar subida de comprobante normal */
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-receipt text-3xl text-orange-600"></i>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800">Paso 4: Enviá tu recibo por WhatsApp</h2>
                    <p className="text-gray-600">Una vez pagado, enviá la foto del recibo por WhatsApp al administrador</p>
                  </div>

                  {/* Info del registro */}
                  {registroActivo && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <i className="fas fa-info-circle text-blue-600 mt-1"></i>
                        <div>
                          <p className="font-medium text-blue-800">Información de tu registro:</p>
                          <ul className="text-sm text-blue-700 mt-2 space-y-1">
                            <li>• Semestre: <strong>{registroActivo.semestre_nombre || registroActivo.semestre}</strong></li>
                            <li>• Método de pago: <strong>{registroActivo.metodo_pago}</strong></li>
                            <li>• Monto: <strong>${registroActivo.monto} {registroActivo.moneda}</strong></li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Aviso si la factura fue generada por el admin */}
                  {registroActivo?.generado_por_admin && (
                    <div className="bg-green-50 border border-green-300 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <i className="fas fa-envelope-open-text text-green-600 mt-1 text-xl"></i>
                        <div>
                          <p className="font-medium text-green-800">Factura enviada por Administración</p>
                          <p className="text-sm text-green-700 mt-1">
                            La administración ha generado tu factura y te la envió por email. 
                            Descárgala abajo y realiza el pago para continuar con tu registro.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botón para descargar invoice */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3">
                        <i className="fas fa-file-invoice text-yellow-600 mt-1"></i>
                        <div>
                          <p className="font-medium text-yellow-800">¿Aún no has pagado?</p>
                          <p className="text-sm text-yellow-700">Descarga tu invoice desde Mis Invoices</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => navigate('/app/mis-invoices')}
                        className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
                      >
                        <i className="fas fa-download mr-2"></i>
                        Ir a Mis Invoices
                      </Button>
                    </div>
                  </div>

                  {/* Estado: ya enviado por WhatsApp */}
                  {registroActivo?.metodo_envio_comprobante === 'whatsapp' || registroActivo?.estado_comprobante === 'enviado_whatsapp' ? (
                    <div className="bg-green-50 border-2 border-green-300 rounded-2xl p-5 text-center" data-testid="whatsapp-enviado-aviso">
                      <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="fab fa-whatsapp text-white text-3xl"></i>
                      </div>
                      <p className="font-bold text-green-800 text-lg">Recibo enviado ✅</p>
                      <p className="text-sm text-green-700 mt-1">
                        Esperando que el administrador confirme tu pago.
                      </p>
                      <p className="text-xs text-green-600 mt-3 italic">
                        Te avisaremos por push y email cuando sea confirmado para que continues.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Instrucciones + botón WhatsApp */}
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-5 md:p-6">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="fab fa-whatsapp text-white text-2xl"></i>
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-800 text-base">Enviar recibo por WhatsApp</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Al hacer click se abre WhatsApp con un mensaje pre-armado con tus datos. Solo agregá la foto del recibo y enviá.
                            </p>
                          </div>
                        </div>

                        <ol className="text-sm text-gray-700 space-y-1.5 mb-4 pl-2">
                          <li><strong>1.</strong> Click en el botón de abajo → se abre WhatsApp</li>
                          <li><strong>2.</strong> Adjuntá la foto/PDF del recibo en la conversación</li>
                          <li><strong>3.</strong> Enviá el mensaje con tu nombre pre-llenado</li>
                          <li><strong>4.</strong> Esperá la confirmación del administrador (llega notificación)</li>
                        </ol>

                        <Button
                          onClick={handleEnviarWhatsApp}
                          disabled={uploadingComprobante}
                          className="w-full h-14 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-black text-base shadow-lg hover:shadow-xl transition-all"
                          data-testid="btn-enviar-whatsapp"
                        >
                          {uploadingComprobante ? (
                            <><i className="fas fa-spinner fa-spin mr-2"></i>Procesando...</>
                          ) : (
                            <><i className="fab fa-whatsapp text-xl mr-2"></i>Abrir WhatsApp y enviar recibo</>
                          )}
                        </Button>

                        <p className="text-[11px] text-gray-500 text-center mt-3 italic">
                          📱 WhatsApp de Administración: +1 646 295 2037
                        </p>
                      </div>
                    </>
                  )}

                  {/* Botón de retroceso */}
                  <Button 
                    variant="outline"
                    onClick={handleRetrocederEtapa}
                    disabled={procesando}
                    className="w-full mt-4 text-gray-600 hover:text-gray-800"
                    data-testid="volver-paso-anterior-comprobante-btn"
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Volver al paso anterior
                  </Button>
                </>
              )}
            </div>
          )}

          {/* ETAPA 4: Inscribir a Clases */}
          {etapaActual === 'inscribir_clases' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-book text-3xl text-purple-600"></i>
                </div>
                <h2 className="text-xl font-bold text-gray-800">Paso 5: Inscripción a Clases</h2>
                <p className="text-gray-600">¡Tu pago fue aprobado! Ahora puedes inscribirte a las clases del semestre.</p>
              </div>

              {/* Lista de clases del semestre */}
              {clasesSemestre.length > 0 ? (
                <div className="space-y-3">
                  <p className="font-medium text-gray-700">Clases disponibles en este semestre:</p>
                  {clasesSemestre.map(clase => (
                    <div key={clase.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <i className="fas fa-chalkboard-teacher text-purple-600"></i>
                        <div>
                          <p className="font-medium">{clase.nombre}</p>
                          <p className="text-sm text-gray-500">{clase.profesor_nombre}</p>
                        </div>
                      </div>
                      <Badge className="bg-purple-100 text-purple-700">{clase.horario || 'Por definir'}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <p>Las clases se asignarán automáticamente</p>
                </div>
              )}

              <Button 
                onClick={handleInscribirClases}
                disabled={inscribiendoClases}
                className="w-full bg-green-600 hover:bg-green-700 py-3 text-lg"
              >
                {inscribiendoClases ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Inscribiendo...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle mr-2"></i>
                    Completar Inscripción
                  </>
                )}
              </Button>
            </div>
          )}

          {/* ETAPA 4: Completado */}
          {etapaActual === 'completado' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-check-circle text-5xl text-green-600"></i>
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-2">¡Inscripción Completada!</h2>
              <p className="text-gray-600 mb-6">Ya estás inscrito en todas las clases del semestre</p>
              
              <div className="flex gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/app/mis-clases')}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <i className="fas fa-book mr-2"></i>
                  Ver Mis Clases
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/app/dashboard')}
                >
                  <i className="fas fa-home mr-2"></i>
                  Ir al Inicio
                </Button>
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Botón de ayuda */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          ¿Tienes problemas? <a href="#" className="text-purple-600 hover:underline">Contactar soporte</a>
        </p>
      </div>
    </div>
  );
};

export default ProcesoRegistro;
